import { useQuery, type QueryClient } from '@tanstack/react-query';

import { useApi } from '../_shared/hooks';
import { getPlaysLibrary } from './api-client';
import { playsLibraryQueryKey, playsQueryKey } from './query-keys';

/**
 * The Library page's plays read (2026-09-17 QA follow-ups, U1). Its own key —
 * NOT a member of the `playsQueryKey(campaignSfdcId)` family `usePlaysForCampaign`
 * uses — so the campaign-scoped lists and this one can be invalidated
 * independently, and so `ReviewStep`'s existing `playsPrefixKey()` prefix
 * invalidation after a send does not refetch the whole Library on every send
 * by accident.
 */
export const PLAYS_LIBRARY_QUERY_KEY = playsLibraryQueryKey();

/**
 * Since QA round 7 (U1) this is the plays read for every LIST surface —
 * Home rows, the Calendar popover and the Library section — and the source
 * of `playbookNames` for the play header. The play flow's own
 * campaign-scoped list (`usePlaysForCampaign`) is a separate,
 * one-per-play-open read.
 */
export function usePlaysLibrary(opts?: { enabled?: boolean }) {
  const { apiFetch } = useApi();
  return useQuery({
    queryKey: PLAYS_LIBRARY_QUERY_KEY,
    queryFn: () => getPlaysLibrary(apiFetch),
    staleTime: Infinity,
    enabled: opts?.enabled ?? true,
  });
}

/**
 * The ONE invalidation every play mutation (create / rename / delete / send
 * status flip) calls: the campaign's own list AND the Library's grouped read.
 * Before this unit each call site invalidated only the campaign key, which
 * would have left the Library section stale after a rename from Home.
 */
export function invalidatePlayLists(queryClient: QueryClient, campaignSfdcId: string): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: playsQueryKey(campaignSfdcId) }),
    queryClient.invalidateQueries({ queryKey: PLAYS_LIBRARY_QUERY_KEY }),
  ]).then(() => undefined);
}
